(async () => {
  if (!customElements.get('sticky-nav')) {
    const { default: Swiper, A11y, Navigation } = await import('swiper');

    Swiper.use([A11y, Navigation]);
    class StickyNav extends HTMLElement {
      constructor() {
        super();
        this.linksArr = this.querySelectorAll('[data-anchor]');
        this.navElement = this.parentElement;
        this.getSections();
        this.initSwiper();
        this.initNavObserver();
      }

      initNavObserver() {
        // Observer to show the nav then it is reached (when position:sticky triggers)
        const navObserverOptions = {
          root: null,
          rootMargin: '0px',
          threshold: 1,
        };

        const navCallback = (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-pinned', entry.intersectionRatio < 1);
          });
        };

        const navObserver = new IntersectionObserver(navCallback, navObserverOptions);

        navObserver.observe(this.navElement);
      }

      // Sections and anchor links
      getSections() {
        // Setup siblings array and get the next sibling to the navElement
        const siblingsArr = [];
        let sibling = this.navElement.nextSibling;

        // Loop through each sibling and push to the array
        while (sibling) {
          // Check if the sibling is an element
          if (sibling.nodeType === 1) {
            siblingsArr.push(sibling);
            // Add a scroll-margin-top property to prevent nav element from overlapping the section content
            sibling.classList.add('scroll-mt-[7.5rem]', 'group-[.header-hidden]/body:scroll-mt-11');
          }
          sibling = sibling.nextSibling;
        }
        this.addAnchors(siblingsArr);
        this.initObserver(siblingsArr);
      }

      addAnchors(sections) {
        this.linksArr = this.querySelectorAll('[data-anchor]');

        this.linksArr.forEach((link, index) => {
          // Find section by the same index in the array
          if (sections[index]) {
            const anchorId = sections[index].id;

            // Add href of the section id
            link.href = `#${anchorId}`;

            // This is a tricky hack invented by me, because the scroll-margin-top didn't have enough time to apply when clicking
            link.addEventListener('click', () => {
              setTimeout(() => {
                sections[index].scrollIntoView({ behavior: 'smooth' });
              }, 100);
            });
          }
        });
      }

      // Swiper
      initSwiper() {
        this.createSwiper();
      }

      createSwiper() {
        this.swiper = this.querySelector('[data-swiper-nav]');

        // Stop when the swiper is not found
        if (!this.swiper) return false;

        // Default to improve user experience
        this.swiperOptions = {
          threshold: 10,
          a11y: true,
          slideToClickedSlide: true,
          slidesPerView: 'auto',
          spaceBetween: 24,
          breakpoints: {
            768: {
              spaceBetween: 32,
            },
          },
        };

        // Check if we have extra options on the HTML
        if (this.swiper.dataset.options) {
          const options = JSON.parse(this.swiper.dataset.options);
          if (options) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...options,
            };
          }
        }

        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
      }

      // IntersectionObserver
      initObserver(sections) {
        // Array for all sections observers
        let observers = [];

        // Options for the observers
        const observerOptions = {
          root: null,
          rootMargin: '0px',
          // Threshold indicates at what percentage of the section's visibility the observer's callback should be executed
          threshold: 0.7,
        };

        const modifyAnchor = (sectionIndex) => {
          this.linksArr.forEach((item, index) => {
            item.classList.remove('sticky-nav__link--active');
            if (index === sectionIndex) {
              item.classList.add('sticky-nav__link--active');
            }
          });
        };

        const intersectionCallback = (entries, i) => {
          entries.forEach((entry) => {
            // If intersection, make the anchor link active
            if (!entry.isIntersecting) return;
            modifyAnchor(i);
          });
        };

        sections.forEach((section, i) => {
          // create the observer
          observers[i] = new window.IntersectionObserver(
            // Passing the index to know with anchor link to highlight
            (e) => intersectionCallback(e, i),
            observerOptions
          );
          // give the observer some dom nodes to keep an eye on
          observers[i].observe(section);
        });
      }
    }

    window.StickyNav = StickyNav;

    customElements.define('sticky-nav', StickyNav);
  }
})();
